// apps/admin/src/pages/billing/CreditMemoListPage.tsx
//
// Phase 8.2 Wave 3 Task 18 — Admin A5 Credit Memos list page.
//
// vg-binding: CONTEXT:decisions=P8.D-485,P8.D-490,P8.D-496,P8.D-498
// vg-binding: INTERFACE-STANDARDS:error-shape
//
//  (A5 surface) +
//      (perm split — `credit_memo:issue:up_to_500` / `:unlimited`
//               `credit_memo:apply` for kế toán + admin) +
//      (PENDING_APPROVAL queue + REJECTED status) +
//      (manual apply via ApplyCreditMemoModal — FIFO invoice picker).
// Covers goals.
//
// Reference design PNG:
//   .vg/phases/04.1-invoice-billing-net-terms/design/screenshots/credit-memo-list.default.png
//   sha256 9980ca52fc109950f6166c16c5f587ba3c72b56890e1045d3f9a8ebdd7bcf451
//
// Endpoints consumed (per Phase 4.1 + API-CONTRACTS):
//   GET    /api/v1/admin/credit-memos?page=&pageSize=&merchant_id=&status=
//   POST   /api/v1/admin/credit-memos             (delegated to IssueCreditMemoModal)
//   POST   /api/v1/admin/credit-memos/:id/approve (PENDING_APPROVAL admin-only)
//   POST   /api/v1/admin/credit-memos/:id/apply   (delegated to ApplyCreditMemoModal)
//   GET    /api/v1/admin/credit-memos/stale       (link from "View stale credit memos")
//
// Error envelope handling per INTERFACE-STANDARDS § FE error envelope:
//   Toast description = `error.user_message || error.message || 'Có lỗi xảy ra'`.
//
// URL state: ?page, ?status, ?merchant_id, ?search — synced via
//   React Router useSearchParams (replace history so back-button works).

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Card,
  CardContent,
  Input,
  MerchantPicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useAuth,
  useToast,
  PageHeader,
} from '@printway/ui';
import { useTranslation } from '../../i18n/useTranslation.js';
import { PageListFooter } from '../../components/datatable/PageListFooter.js';

import {
  ApplyCreditMemoModal,
  type ApplyCreditMemoTarget,
} from '../../features/billing/ApplyCreditMemoModal.js';
import { IssueCreditMemoModal } from '../../features/billing/IssueCreditMemoModal.js';
import { StatusBadge, type StatusTone } from '../../components/ui/StatusBadge.js';

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

/**
 * 7-status filter set per task-18 acceptance (498/496 aligned):
 *   ACTIVE, APPLIED (BE: FULLY_APPLIED), PARTIALLY_APPLIED, CONVERTED,
 *   REFUNDED_TO_BANK, PENDING_APPROVAL, REJECTED.
 *
 * BE Wave 3 schema enum (merchant-list.handler.ts):
 *   ACTIVE | FULLY_APPLIED | EXPIRED | CONVERTED | REFUNDED_TO_BANK |
 *   PARTIALLY_APPLIED | PENDING_APPROVAL
 *
 * The UI maps the spec label "APPLIED" → BE "FULLY_APPLIED" on send +
 * accepts both on receive so memos issued under either label render
 * consistently. REJECTED is the admin-reject terminal state.
 */
type CreditMemoStatusEnum =
  | 'ACTIVE'
  | 'APPLIED'
  | 'FULLY_APPLIED'
  | 'PARTIALLY_APPLIED'
  | 'CONVERTED'
  | 'REFUNDED_TO_BANK'
  | 'PENDING_APPROVAL'
  | 'REJECTED'
  // Legacy Wave 3 BE values still surfaced as-is for back-compat.
  | 'EXHAUSTED'
  | 'EXPIRED'
  | 'DORMANT'
  | 'VOIDED';

type CreditMemoIssueTypeEnum =
  | 'MANUAL_GOODWILL'
  | 'AUTO_REFUND'
  | 'AUTO_OVERPAYMENT'
  | 'NEGOTIATED_CREDIT';

type CreditMemoDormancyEnum = 'active' | 'dormant';

interface CreditMemoRow {
  id: string;
  merchant_id: string;
  merchant_name?: string | null;
  merchant_email?: string | null;
  amount_usd_cents: number;
  remaining_usd_cents: number;
  status: CreditMemoStatusEnum;
  issue_type: CreditMemoIssueTypeEnum;
  dormancy_status?: CreditMemoDormancyEnum | null;
  notes?: string | null;
  applied_invoice_number?: string | null;
  original_order_id?: string | null;
  created_at: string;
  last_activity_at?: string | null;
}

interface PaginationMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface CreditMemoListResponseBody {
  credit_memos: CreditMemoRow[];
  pagination: PaginationMeta;
  totals?: {
    active_count: number;
    pending_approval_count: number;
    active_remaining_usd_cents: number;
  };
}

interface ApiErrorEnvelope {
  ok?: false;
  success?: false;
  error?: {
    code?: string;
    message?: string;
    user_message?: string;
  };
}

type LooseClient = {
  GET: (
    path: string,
    init?: { headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  POST: (
    path: string,
    init: { body?: unknown; headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Spec 7-status filter (task-18 acceptance). Order matches design PNG.
const STATUS_OPTIONS: Array<{ value: '' | CreditMemoStatusEnum; labelKey: string }> = [
  { value: '', labelKey: 'credit_memo.filters.allStatuses' },
  { value: 'ACTIVE', labelKey: 'credit_memo.filterOptions.active' },
  { value: 'APPLIED', labelKey: 'credit_memo.filterOptions.applied' }, // BE: FULLY_APPLIED
  { value: 'PARTIALLY_APPLIED', labelKey: 'credit_memo.filterOptions.partiallyApplied' },
  { value: 'CONVERTED', labelKey: 'credit_memo.filterOptions.converted' },
  { value: 'REFUNDED_TO_BANK', labelKey: 'credit_memo.filterOptions.refundedToBank' },
  { value: 'PENDING_APPROVAL', labelKey: 'credit_memo.filterOptions.pendingApproval' },
  { value: 'REJECTED', labelKey: 'credit_memo.filterOptions.rejected' },
];

// Phase 8.2 audit 2026-05-23 — DEFAULT_PAGE_SIZE bumped 20 → 25 to match the
// canonical /stores PageListFooter pageSizeChoices [10, 25, 50, 100]. Live
// value flows from URL-synced `pageSize` searchParam (clamped 10..100).
const DEFAULT_PAGE_SIZE = 25;

// Radix forbids SelectItem value="" — sentinel for the "all statuses" option.
const ALL_VALUE = '__all__';

// 12-month dormancy window cron output.
const STALE_THRESHOLD_MS = 365 * 24 * 60 * 60 * 1000;

function formatUsdCentsSigned(cents: number, signNegative = true): string {
  const abs = Math.abs(cents) / 100;
  const formatted = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(abs);
  return signNegative ? `-${formatted}` : formatted;
}

function formatDateOnly(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Maps each issue-type enum to its i18n key. Unknown values fall back to the
// raw backend enum string at the call site.
const REASON_KEYS: Record<CreditMemoIssueTypeEnum, string> = {
  MANUAL_GOODWILL: 'credit_memo.reason.goodwill',
  AUTO_REFUND: 'credit_memo.reason.refund',
  AUTO_OVERPAYMENT: 'credit_memo.reason.overpayment',
  NEGOTIATED_CREDIT: 'credit_memo.reason.negotiated',
};

/**
 * Convert the UI status filter to a BE query string value.
 * - "APPLIED" (spec) → "FULLY_APPLIED" (BE Wave 3 enum).
 * - All others pass through unchanged.
 */
function toBackendStatus(uiStatus: '' | CreditMemoStatusEnum): string {
  if (uiStatus === 'APPLIED') return 'FULLY_APPLIED';
  return uiStatus;
}

function extractApiMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object') {
    const env = payload as ApiErrorEnvelope;
    if (env.error?.user_message) return env.error.user_message;
    if (env.error?.message) return env.error.message;
  }
  return fallback;
}

function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `idemp-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 *  acceptance: "StaleAlertBadge red when ACTIVE > 12 months".
 * Returns true when a memo is in ACTIVE status AND has been so for > 12mo.
 * Falls back to `dormancy_status === 'dormant'` when BE has tagged it.
 */
function isStale(row: CreditMemoRow): boolean {
  if (row.dormancy_status === 'dormant' || row.status === 'DORMANT') return true;
  if (row.status !== 'ACTIVE') return false;
  const anchorIso = row.last_activity_at || row.created_at;
  const t = new Date(anchorIso).getTime();
  if (!Number.isFinite(t)) return false;
  return Date.now() - t > STALE_THRESHOLD_MS;
}

// ---------------------------------------------------------------------------
// CreditMemoStatusBadge — colour variants for the 7-status set + legacy.
// ---------------------------------------------------------------------------

// Phase 8.x Debug B3 — switched the ad-hoc Tailwind triples to the canonical
// `<StatusBadge>` so credit-memo pills match every other status pill in the
// admin app (soft-tint + ring + dot indicator).
function CreditMemoStatusBadge({
  status,
}: {
  status: CreditMemoStatusEnum;
}): JSX.Element {
  const { t } = useTranslation('finance');
  let tone: StatusTone;
  let label: string;
  switch (status) {
    case 'ACTIVE':
      tone = 'info';
      label = t('credit_memo.badge.active');
      break;
    case 'PENDING_APPROVAL':
      tone = 'pending';
      label = t('credit_memo.badge.pending');
      break;
    case 'APPLIED':
    case 'FULLY_APPLIED':
      tone = 'approved';
      label = t('credit_memo.badge.applied');
      break;
    case 'PARTIALLY_APPLIED':
      tone = 'approved';
      label = t('credit_memo.badge.partial');
      break;
    case 'CONVERTED':
      tone = 'info';
      label = t('credit_memo.badge.converted');
      break;
    case 'REFUNDED_TO_BANK':
      tone = 'info';
      label = t('credit_memo.badge.refunded');
      break;
    case 'REJECTED':
      tone = 'rejected';
      label = t('credit_memo.badge.rejected');
      break;
    case 'EXHAUSTED':
    case 'EXPIRED':
      tone = 'approved';
      label = t('credit_memo.badge.applied');
      break;
    case 'DORMANT':
      tone = 'at_risk';
      label = t('credit_memo.badge.dormant');
      break;
    case 'VOIDED':
      tone = 'closed';
      label = t('credit_memo.badge.voided');
      break;
    default:
      tone = 'neutral';
      label = String(status);
      break;
  }
  return <StatusBadge tone={tone} label={label} />;
}

// ---------------------------------------------------------------------------
// StaleAlertBadge — RED variant per task-18 spec ("StaleAlertBadge red
// when ACTIVE > 12 months"). 12-month dormancy is surfaced via the cron
// in Wave 11 Task 34 OR computed FE-side from last_activity_at/created_at.
// ---------------------------------------------------------------------------

function StaleAlertBadge(): JSX.Element {
  const { t } = useTranslation('finance');
  return (
    <span
      className="ml-2 inline-flex items-center gap-1 rounded-md border border-red-300 bg-red-50 dark:bg-red-950/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-red-700 dark:text-red-300"
      title={t('credit_memo.stale.tooltip')}
      aria-label={t('credit_memo.stale.ariaLabel')}
      data-testid="credit-memo-stale-badge"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-3 w-3"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      {t('credit_memo.stale.label')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CreditMemoListPage(): JSX.Element {
  const { t } = useTranslation('finance');
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuth((s) => s.user);
  const permissions = useAuth((s) => s.permissions);
  const isAccountant = user?.primaryRole === 'accountant';
  const isAdmin = user?.primaryRole === 'admin';

  // ---- URL state ----
  const page = Math.max(1, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1);
  // Phase 8.2 audit 2026-05-23 — pageSize is URL-synced per /stores standard,
  // clamped to the allowlist so a hand-typed value can't break the BE cap.
  const rawPageSize = Number.parseInt(
    searchParams.get('pageSize') ?? String(DEFAULT_PAGE_SIZE),
    10,
  );
  const pageSize = Number.isFinite(rawPageSize) && rawPageSize > 0
    ? Math.min(100, Math.max(10, rawPageSize))
    : DEFAULT_PAGE_SIZE;
  const status = (searchParams.get('status') ?? '') as '' | CreditMemoStatusEnum;
  const merchantId = searchParams.get('merchant_id') ?? '';
  const search = searchParams.get('search') ?? '';

  // ---- Local state ----
  const [rows, setRows] = useState<CreditMemoRow[]>([]);
  const [pagination, setPaginationState] = useState<PaginationMeta>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [searchInput, setSearchInput] = useState(search);

  // ---- Modal state ----
  const [issueOpen, setIssueOpen] = useState(false);
  const [applyMemo, setApplyMemo] = useState<ApplyCreditMemoTarget | null>(null);

  // ---- Per-row approve loading state ----
  const [approvingId, setApprovingId] = useState<string | null>(null);

  // Sync search input when URL changes externally (back/forward).
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  // ---- URL helpers ----
  const updateParam = useCallback(
    (key: string, value: string): void => {
      const next = new URLSearchParams(searchParams);
      if (value === '' || value === undefined) next.delete(key);
      else next.set(key, value);
      // Any filter change resets page to 1.
      if (key !== 'page') next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setPage = useCallback(
    (n: number): void => {
      const next = new URLSearchParams(searchParams);
      if (n <= 1) next.delete('page');
      else next.set('page', String(n));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = useCallback((): void => {
    setSearchParams(new URLSearchParams(), { replace: true });
    setSearchInput('');
  }, [setSearchParams]);

  // ---- List fetch ----
  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    p.set('page', String(page));
    p.set('pageSize', String(pageSize));
    if (status) {
      const be = toBackendStatus(status);
      if (be) p.set('status', be);
    }
    if (merchantId) p.set('merchant_id', merchantId);
    if (search) p.set('search', search);
    return p.toString();
  }, [page, pageSize, status, merchantId, search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async (): Promise<void> => {
      try {
        const client = apiClient() as unknown as LooseClient;
        const { data, error, response } = await client.GET(
          `/api/v1/admin/credit-memos?${queryString}`,
        );
        if (cancelled) return;
        if (error || !response.ok) {
          toast({
            title: t('credit_memo.toast.loadFailed'),
            description: extractApiMessage(error, t('credit_memo.toast.genericError')),
            variant: 'error',
          });
          setRows([]);
          setPaginationState({
            page: 1,
            pageSize: DEFAULT_PAGE_SIZE,
            total: 0,
            totalPages: 0,
          });
          return;
        }
        const body = data as
          | { ok?: true; data?: CreditMemoListResponseBody }
          | CreditMemoListResponseBody
          | undefined;
        const inner: CreditMemoListResponseBody | undefined =
          body && typeof body === 'object' && 'data' in body && body.data
            ? body.data
            : (body as CreditMemoListResponseBody | undefined);
        if (inner && Array.isArray(inner.credit_memos)) {
          setRows(inner.credit_memos);
          setPaginationState(
            inner.pagination ?? {
              page,
              pageSize: DEFAULT_PAGE_SIZE,
              total: inner.credit_memos.length,
              totalPages: 1,
            },
          );
        } else {
          setRows([]);
          setPaginationState({
            page: 1,
            pageSize: DEFAULT_PAGE_SIZE,
            total: 0,
            totalPages: 0,
          });
        }
      } catch (err) {
        if (cancelled) return;
        toast({
          title: t('credit_memo.toast.networkError'),
          description:
            err instanceof Error && err.message
              ? err.message
              : t('credit_memo.toast.networkErrorDesc'),
          variant: 'error',
        });
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [queryString, refreshTick, toast, page, t]);

  // ---- Approve PENDING_APPROVAL memo (admin-only) ----
  async function handleApprove(memoId: string): Promise<void> {
    if (!isAdmin) {
      toast({
        title: t('credit_memo.toast.adminRequired'),
        description: t('credit_memo.toast.adminRequiredDesc'),
        variant: 'warning',
      });
      return;
    }
    setApprovingId(memoId);
    try {
      const client = apiClient() as unknown as LooseClient;
      const { error, response } = await client.POST(
        `/api/v1/admin/credit-memos/${encodeURIComponent(memoId)}/approve`,
        {
          body: {},
          headers: { 'Idempotency-Key': newIdempotencyKey() },
        },
      );
      if (error || !response.ok) {
        toast({
          title: t('credit_memo.toast.approveFailed'),
          description: extractApiMessage(error, t('credit_memo.toast.genericError')),
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('credit_memo.toast.approved'),
        description: t('credit_memo.toast.approvedDesc'),
        variant: 'success',
      });
      setRefreshTick((prev) => prev + 1);
    } catch (err) {
      toast({
        title: t('credit_memo.toast.networkError'),
        description:
          err instanceof Error && err.message
            ? err.message
            : t('credit_memo.toast.networkErrorDesc'),
        variant: 'error',
      });
    } finally {
      setApprovingId(null);
    }
  }

  // ---- Render ----
  const hasAnyFilter = !!(status || merchantId || search);

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Page header */}
      <PageHeader
        title={t('credit_memo.title')}
        subtitle={t('credit_memo.list.subtitle')}
        actions={
          <>
            <Link
              to="/admin/billing/credit-memo-approvals"
              className="inline-flex h-11 items-center justify-center rounded-lg border border-pw-border bg-pw-bg px-4 text-sm font-medium text-pw-text-muted transition-colors hover:bg-pw-surface hover:text-pw-text"
              data-testid="credit-memo-view-pending-link"
              title={t('credit_memo.list.pendingApprovalLinkTooltip')}
            >
              {t('credit_memo.list.pendingApprovalLink')}
            </Link>
            <button
              type="button"
              onClick={() => setIssueOpen(true)}
              className="inline-flex h-11 items-center justify-center rounded-lg bg-pw-primary px-5 text-sm font-medium text-white transition-colors hover:bg-pw-primary-hover"
              data-testid="credit-memo-issue-btn"
            >
              + {t('credit_memo.actions.issue')}
            </button>
          </>
        }
      />

      {/* Filter bar */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 py-4">
          <div className="min-w-[260px] flex-1">
            <label className="mb-1 block text-xs font-medium text-pw-text-muted">
              {t('credit_memo.filters.searchLabel')}
            </label>
            <Input
              type="search"
              value={searchInput}
              placeholder={t('credit_memo.filters.searchPlaceholder')}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') updateParam('search', searchInput.trim());
              }}
              onBlur={() => {
                if (searchInput.trim() !== search) {
                  updateParam('search', searchInput.trim());
                }
              }}
              aria-label={t('credit_memo.filters.searchAria')}
            />
          </div>
          <div className="min-w-[180px]">
            <label
              className="mb-1 block text-xs font-medium text-pw-text-muted"
              htmlFor="admin-cm-status-filter"
            >
              {t('credit_memo.columns.status')}
            </label>
            <Select
              value={status === '' ? ALL_VALUE : status}
              onValueChange={(value) =>
                updateParam('status', value === ALL_VALUE ? '' : value)
              }
            >
              <SelectTrigger
                id="admin-cm-status-filter"
                data-testid="admin-cm-status-filter"
                aria-label={t('credit_memo.filters.statusAria')}
              >
                <SelectValue placeholder={t('credit_memo.filters.allStatuses')} />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem
                    key={opt.value || 'all'}
                    value={opt.value === '' ? ALL_VALUE : opt.value}
                  >
                    {t(opt.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[200px]">
            <label
              className="mb-1 block text-xs font-medium text-pw-text-muted"
              htmlFor="admin-cm-merchant-filter"
            >
              {t('credit_memo.columns.merchant')}
            </label>
            <MerchantPicker
              value={merchantId || null}
              valueLabel={null}
              onChange={(id) => updateParam('merchant_id', id ?? '')}
              billingMode="INVOICE"
              placeholder={t('credit_memo.filters.allMerchants')}
              testId="admin-cm-merchant-filter"
            />
          </div>
          {hasAnyFilter && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              {t('credit_memo.filters.clear')}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Table card */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table
              className="w-full text-sm"
              data-testid="credit-memo-list-table"
            >
              <thead className="border-b border-pw-border bg-pw-surface/60 text-left text-xs font-medium uppercase tracking-wide text-pw-text-muted">
                <tr>
                  <th className="px-4 py-3">{t('credit_memo.columns.number')}</th>
                  <th className="px-4 py-3">{t('credit_memo.columns.merchant')}</th>
                  <th className="px-4 py-3">{t('credit_memo.columns.reason')}</th>
                  <th className="px-4 py-3">{t('credit_memo.columns.amount')}</th>
                  <th className="px-4 py-3">{t('credit_memo.columns.appliedTo')}</th>
                  <th className="px-4 py-3">{t('credit_memo.columns.status')}</th>
                  <th className="px-4 py-3">{t('credit_memo.columns.issuedAt')}</th>
                  <th className="w-[160px] px-4 py-3 text-right">
                    {t('credit_memo.columns.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-12 text-center text-pw-text-muted"
                    >
                      {t('credit_memo.list.loading')}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-12 text-center text-pw-text-muted"
                    >
                      {hasAnyFilter
                        ? t('credit_memo.empty.filtered')
                        : t('credit_memo.empty.none')}
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    const stale = isStale(row);
                    const isPending = row.status === 'PENDING_APPROVAL';
                    const canApply =
                      row.status === 'ACTIVE' ||
                      row.status === 'PARTIALLY_APPLIED';
                    return (
                      <tr
                        key={row.id}
                        data-testid={`credit-memo-row-${row.id}`}
                        className="border-b border-pw-border last:border-b-0 transition-colors hover:bg-pw-surface/50"
                      >
                        <td
                          className="px-4 py-4 align-middle font-mono text-xs text-pw-text"
                          title={row.id}
                        >
                          CM-…{row.id.slice(-6)}
                        </td>
                        <td className="px-4 py-4 align-middle">
                          <div
                            className="font-semibold text-pw-text"
                            title={row.merchant_id}
                          >
                            {row.merchant_name || '—'}
                          </div>
                          {row.merchant_email ? (
                            <div className="text-xs text-pw-text-muted">
                              {row.merchant_email}
                            </div>
                          ) : null}
                          <div className="font-mono text-[10px] text-pw-text-muted/70">
                            …{row.merchant_id.slice(-6)}
                          </div>
                        </td>
                        <td className="px-4 py-4 align-middle text-pw-text">
                          {REASON_KEYS[row.issue_type]
                            ? t(REASON_KEYS[row.issue_type])
                            : row.issue_type}
                        </td>
                        <td className="px-4 py-4 align-middle font-medium text-rose-600 dark:text-rose-300">
                          {formatUsdCentsSigned(row.amount_usd_cents)}
                        </td>
                        <td className="px-4 py-4 align-middle text-pw-text-muted">
                          {row.applied_invoice_number || '—'}
                        </td>
                        <td className="px-4 py-4 align-middle">
                          <div className="flex items-center">
                            <CreditMemoStatusBadge status={row.status} />
                            {stale ? <StaleAlertBadge /> : null}
                          </div>
                        </td>
                        <td className="px-4 py-4 align-middle text-pw-text">
                          {formatDateOnly(row.created_at)}
                        </td>
                        <td className="px-4 py-4 align-middle text-right">
                          <div className="flex items-center justify-end gap-2">
                            {isPending && isAdmin ? (
                              <button
                                type="button"
                                onClick={() => void handleApprove(row.id)}
                                disabled={approvingId === row.id}
                                data-testid={`credit-memo-approve-btn-${row.id}`}
                                className="inline-flex h-8 items-center rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 px-2.5 text-xs font-medium text-emerald-700 dark:text-emerald-300 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {approvingId === row.id
                                  ? t('credit_memo.actions.approving')
                                  : t('credit_memo.approvals.approve')}
                              </button>
                            ) : null}
                            {canApply ? (
                              <button
                                type="button"
                                onClick={() =>
                                  setApplyMemo({
                                    id: row.id,
                                    merchant_id: row.merchant_id,
                                    remaining_usd_cents: row.remaining_usd_cents,
                                  })
                                }
                                data-testid={`credit-memo-apply-btn-${row.id}`}
                                className="inline-flex h-8 items-center rounded-md border border-pw-border px-2.5 text-xs font-medium text-pw-text-muted transition-colors hover:bg-pw-surface hover:text-pw-text"
                                title={t('credit_memo.actions.applyTooltip')}
                              >
                                {t('credit_memo.actions.applyShort')}
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination — canonical /stores footer (Rows-per-page + Total +
              numbered) per audit 2026-05-23 Cluster A. */}
          <div className="border-t border-pw-border px-6 py-4">
            <PageListFooter
              page={pagination.page || page}
              pageSize={pageSize}
              total={pagination.total}
              totalPages={Math.max(1, pagination.totalPages || 1)}
              loading={loading}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                const next = new URLSearchParams(searchParams);
                next.set('pageSize', String(size));
                next.delete('page');
                setSearchParams(next, { replace: true });
              }}
              testId="credit-memo-footer"
            />
          </div>
        </CardContent>
      </Card>

      {/* Extracted modals (Task 18 — sibling components/498). */}
      <IssueCreditMemoModal
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        defaultMerchantId={merchantId}
        userPermissions={permissions}
        isAccountant={isAccountant}
        onIssued={() => setRefreshTick((prev) => prev + 1)}
      />
      <ApplyCreditMemoModal
        memo={applyMemo}
        open={!!applyMemo}
        onClose={() => setApplyMemo(null)}
        onApplied={() => setRefreshTick((prev) => prev + 1)}
      />
    </div>
  );
}

export default CreditMemoListPage;
